import React from 'react';
import { Box } from '@mui/material';
import { CreateFavouriteListFormValues } from 'components/common/Favourite/CreateFavouriteListPopUp';
import { SparkReportFormValues } from 'components/common/Report/SparkReport';
import { ReviewFormModalValues } from 'components/common/ReviewFormModal';
import SparkOptionsModal from 'components/common/SparkOptionsModal';
import { useCurrentUser } from 'hooks/common';
import { useFlagReasons } from 'hooks/EntityTypes/useFlagReasons';
import {
  useCreateFavouriteList,
  useCreateSparkFavourite,
  useDeleteSparkFavourite,
} from 'hooks/Favourites';
import {
  useAddSparkRating,
  useUpdateSpark,
  useUpdateSparkRating,
} from 'hooks/spark';
import { useAddUserFlag } from 'hooks/userFlags/useAddUserFlag';
import type { JoinedSparks } from 'types/Spark/JoinedSpark';
import { getCurrentDateISOString } from 'utilities/time';

type SparkRowOptionsProps = {
  joinedSpark: JoinedSparks | undefined;
};
function SparkRowOptions({ joinedSpark }: SparkRowOptionsProps) {
  const existingFavouriteList = joinedSpark?.meta.user.favouriteLists;
  const { ratings, permissions } = joinedSpark?.meta.user || {};
  const allowedToReview = permissions?.canReview
  const userRating = ratings?.find(
    (rating) => rating.sparkID === joinedSpark?.id
  );
  const { mutateAsync: updateSpark } = useUpdateSpark(joinedSpark?.id);

  const { mutateAsync: addSparkRating } = useAddSparkRating();
  const { mutateAsync: updateSparkRating } = useUpdateSparkRating(
    userRating?.id ? `${userRating?.id}` : undefined
  );
  const { currentUserID } = useCurrentUser();

  const { mutateAsync: createFavoriteList } = useCreateFavouriteList();

  const {
    mutateAsync: deleteSparkFavorites,
    isLoading: isDeletingSparkFavourite,
  } = useDeleteSparkFavourite();

  const { mutateAsync: addUserFlag } = useAddUserFlag();
  const { dropdownOptions: flagReasonsDropdownOptions } = useFlagReasons();

  const {
    mutateAsync: createSparkFavorites,
    isLoading: isCreatingSparkFavourite,
  } = useCreateSparkFavourite();
  const handleCreateList = async (values: CreateFavouriteListFormValues) => {
    const res = await createFavoriteList(values.listName);

    const sparkFavourite = {
      sparkID: joinedSpark?.id,
      userID: currentUserID,
      favouriteListID: Number(res.data.id),
    };
    await createSparkFavorites(sparkFavourite);
  };

  const handleFavouriteListItem = async (listID: number) => {
    if (existingFavouriteList?.some((listItem) => listItem.id === listID)) {
      const sparkFavourite = joinedSpark?.sparkFavourites?.find(
        (favourite) => favourite.attributes.favouriteListID === listID
      );

      await deleteSparkFavorites(sparkFavourite?.id || '');
      return;
    }
    const sparkFavourite = {
      sparkID: joinedSpark?.id,
      userID: currentUserID,
      favouriteListID: listID,
    };
    await createSparkFavorites(sparkFavourite);
  };

  const handleSparkRating = async (values: ReviewFormModalValues) => {
    const doesReviewExists = !!userRating;
    if (!doesReviewExists) {
      await addSparkRating({ ...values, sparkID: joinedSpark?.id });
    }
    if (doesReviewExists) {
      await updateSparkRating({ ...values, sparkID: joinedSpark?.id });
    }
  };

  const handleSendReport = async (values: SparkReportFormValues) => {
    const userFlag = {
      flagReasonID: +values.reasonID,
      sparkID: joinedSpark?.id,
      comments: values.comments,
    };
    await addUserFlag(userFlag);
  };
  const handleDeleteSpark = async () => {
    await updateSpark({
      deletedAt: getCurrentDateISOString(),
      isDeleted: true,
    });
  };

  const handleArchiveSpark = async () => {
    await updateSpark({
      isArchived: true,
    });
  };
  const userPermissions = joinedSpark?.meta.user.permissions;

  const isLoadingSparkFavourites =
    isCreatingSparkFavourite || isDeletingSparkFavourite;

  return (
    <Box
      display='flex'
      alignItems='center'
      gap={2}
      justifyContent='flex-end'
      width='100%'
    >
      <SparkOptionsModal
        resourceLogoURL={joinedSpark?.attributes.iconURL || ''}
        privacyTypeId={joinedSpark?.attributes.sparkPrivacyTypeID}
        resourceAccessLinkToken={joinedSpark?.attributes.accessLinkToken}
        options={joinedSpark?.meta.options}
        resourceID={joinedSpark?.id}
        handleSparkRatingSave={handleSparkRating}
        userRating={userRating}
        allowedToReview={allowedToReview}
        resourceType='spark'
        onFavouriteItemClick={handleFavouriteListItem}
        existingFavouriteList={existingFavouriteList || []}
        onCreateList={handleCreateList}
        isLoading={isLoadingSparkFavourites}
        onSendReport={handleSendReport}
        flagReasonsDropdownOptions={flagReasonsDropdownOptions}
        onDelete={handleDeleteSpark}
        onArchive={handleArchiveSpark}
        showHeartIcon={false}
        userPermissions={userPermissions}
        showShareIcon={false}
        resourceName={joinedSpark?.attributes?.nameforHuman || '- -'}
      />
    </Box>
  );
}

export default SparkRowOptions;
